import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Moment from 'moment';
import { extendMoment } from 'moment-range';

import Grid from '@material-ui/core/Grid';
import { getFormValues } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import Progress from 'components/common/Progress';

import EraChartStats from './EraChartStats';
import EraChartPayersForm from './EraChartPayersForm';
import EraChartDoughnut from './EraChartDoughnut';
import EraChartDenialReasons from './EraChartDenialReasons';
import EraChartERATable from './EraChartERATable';
import EraChartClaimsTable from './EraChartClaimsTable';
import EraChartFilterForm from './EraChartFilterForm';

import dateFormat from 'config/dateFormat';

import { fetchEraStatistics, fetchEra, getEraDocument } from 'redux/modules/eraStatistics';

import pxToRem from 'styles/pxToRem';

const moment = extendMoment(Moment);

const styles = theme => ({
  root: {
    position: 'relative',
    minHeight: 434,
    maxHeight: '100%',
    boxSizing: 'border-box',
  },
  typographyCaption: {
    textTransform: 'uppercase',
  },
  gridItem: {
    borderRight: `1px solid ${theme.palette.primary.A100}`,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    padding: '8px 16px',
    '&:last-child': {
      borderRight: 'none',
    },
    '&:nth-child(4n)': {
      borderRight: 'none',
    },
  },
  gridItemPayersForm: {
    minHeight: 287,
    borderRight: `1px solid ${theme.palette.primary.A100}`,
  },
  gridItemEmpty: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  typographyPaid: {
    color: theme.palette.states.paid,
  },
  typographyPartial: {
    color: theme.palette.states.intake,
  },
  typographyDenied: {
    color: theme.palette.states.rejected,
  },
  tablesContainer: {
    position: 'relative',
  },
  typographyHeaderTitle: {
    fontSize: pxToRem(18),
    marginTop: 2,
    marginRight: 16,
    marginBottom: 4,
  },
  header: {
    display: 'flex',
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  typographyTodayInfoCount: {
    marginTop: -4,
  },
  gridItemShrink: {
    flexShrink: 1,
  },
  gridItemFilterForm: {
    maxWidth: '100%',
  },
});

class EraChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dateFrom: moment().format(dateFormat),
      dateTo: moment().format(dateFormat),
      openedEraCheckNumber: null,
      todayERACount: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onERADetailsLoad = this.onERADetailsLoad.bind(this);
    this.onERADownload = this.onERADownload.bind(this);
    this.onClaimsTableBackButtonClick = this.onClaimsTableBackButtonClick.bind(this);
  } 

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filterFormValues, eraData, loading } = this.props;
    if ((nextProps.filterFormValues['Date From'] !== filterFormValues['Date From']
      || nextProps.filterFormValues['Date To'] !== filterFormValues['Date To'])
      && moment(nextProps.filterFormValues['Date From'], dateFormat).isValid()
      && moment(nextProps.filterFormValues['Date To'], dateFormat).isValid()) {
      this.setState({
        dateFrom: moment(nextProps.filterFormValues['Date From'], dateFormat).format(dateFormat),
        dateTo: moment(nextProps.filterFormValues['Date To'], dateFormat).format(dateFormat),
      });
    }
    if (nextProps.loading === 0
      && Object.keys(nextProps.eraData).length > 0
      && Object.keys(eraData).length > 0) {
      const todayERACount = Object.keys(eraData).reduce((accumulator, currentValue) =>
        accumulator + Object.keys(eraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
          [moment().format(dateFormat), moment().subtract(1, 'day').format(dateFormat)]
            .indexOf(eraData[currentValue][eraCurrentValue].Date) !== -1
          ? eraAccumulator + 1 : eraAccumulator, 0), 0);
      this.setState({ todayERACount });
    }
  } 

  componentDidUpdate(prevProps, prevState) {
    const { dateFrom, dateTo } = this.state;
    if (prevState.dateFrom !== dateFrom || prevState.dateTo !== dateTo) {
      this.onRequest();
    }
  }

  onRequest() {
    const { actions } = this.props;
    const { dateFrom, dateTo } = this.state;
    actions.fetchEraStatistics({
      triggerLoading: true,
      'Info Type': 'ClaimMD ERA Info',
      'filters': [
        {
          name: 'Create From',
          operation: 'ge',
          val: dateFrom,
        },
        {
          name: 'Create To',
          operation: 'le',
          val: dateTo === dateFrom ? moment(dateTo).add(1, 'day').format(dateFormat) : dateTo,
        },
      ],
    });
  }

  onERADetailsLoad(check_number) {
    this.props.actions.fetchEra({
      check_number,
    });
    this.setState({
      openedEraCheckNumber: check_number,
    });
  }

  onERADownload(id) {
    this.props.actions.getEraDocument({
      'ERA ID': id,
    });
  }

  onClaimsTableBackButtonClick() {
    this.setState({
      openedEraCheckNumber: null,
    });
  }

  render() {
    const { classes, eraData, payersFormValues, theme, loading } = this.props;
    const { dateFrom, dateTo, openedEraCheckNumber, todayERACount } = this.state;
    const filteredEraData = Object.keys(eraData).reduce((accumulator, currentValue) => ({
        ...accumulator,
        ...payersFormValues[currentValue] ? { [currentValue]: eraData[currentValue] } : {},
      }), {});
    const denialReasons = Object.keys(eraData).reduce((accumulator, payerName) => {
      Object.keys(eraData[payerName]).forEach((eraNumber) => {
        const carc = eraData[payerName][eraNumber].Denied && eraData[payerName][eraNumber].Denied.CARC
          ? eraData[payerName][eraNumber].Denied.CARC : {};
        Object.keys(carc).forEach((carcCode) => {
          accumulator[carcCode] = (accumulator[carcCode] || 0) + carc[carcCode];
        });
      });
      return accumulator;
    }, {});
    const openedEra = openedEraCheckNumber
      ? Object.keys(filteredEraData).reduce((found, payerName) => (
        found
          || (filteredEraData[payerName][openedEraCheckNumber]
            && filteredEraData[payerName][openedEraCheckNumber].ERA
            ? filteredEraData[payerName][openedEraCheckNumber]
            : null)
          || null
      ), null)
      : null;
    return (
      <Paper className={classes.root}>
        <div className={classes.header}>
          <Grid container spacing={0} justify="space-between" wrap="nowrap">
            <Grid item className={classes.gridItemShrink}>
              <Grid container spacing={0} wrap="wrap">
                <Grid item>
                  <Typography className={classes.typographyHeaderTitle}>
                    ERA analytics
                  </Typography>
                </Grid>
                <Grid className={classes.gridItemFilterForm} item>
                  <EraChartFilterForm
                    form="eraChartFilterForm"
                    initialValues={{
                      'Date Range': 'today',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <div className={classes.todayInfo}>
                <Typography variant="caption">
                  Today ERAs
                </Typography>
                <Typography className={classes.typographyTodayInfoCount}>
                  {todayERACount} received
                </Typography>
              </div>
            </Grid>
          </Grid>
        </div>
        <EraChartStats filteredEraData={filteredEraData} />
        <Grid container spacing={0}>
          <Grid item xs={4} className={classes.gridItemPayersForm}>
            <EraChartPayersForm
              form="eraChartPayersForm"
              formValues={payersFormValues}
              payers={Object.keys(eraData).map(key => ({
                Name: key,
                ...Object.keys(eraData[key]).reduce((accumulator, currentValue) => {
                  return {
                    'Paid Claims Count': eraData[key][currentValue].Paid ? accumulator['Paid Claims Count']
                      + eraData[key][currentValue].Paid['Claims Count'] : accumulator['Paid Claims Count'],
                    'Partial Claims Count': eraData[key][currentValue]['Partial Regular'] ? accumulator['Partial Claims Count']
                      + eraData[key][currentValue]['Partial Regular']['Claims Count'] : accumulator['Partial Claims Count'],
                    'Denied Claims Count': eraData[key][currentValue].Denied ? accumulator['Denied Claims Count']
                      + eraData[key][currentValue].Denied['Claims Count'] : accumulator['Denied Claims Count'],
                    'Paid Reimbs': accumulator['Paid Reimbs']
                      + (eraData[key][currentValue].Paid ? eraData[key][currentValue].Paid['Paid Reimbs'] : 0)
                      + (eraData[key][currentValue]['Partial Regular'] ? eraData[key][currentValue]['Partial Regular']['Paid Reimbs'] : 0)
                      + (eraData[key][currentValue].Denied ? eraData[key][currentValue].Denied['Paid Reimbs'] : 0),
                  };
                }, {
                    'Paid Claims Count': 0,
                    'Partial Claims Count': 0,
                    'Denied Claims Count': 0,
                    'Paid Reimbs': 0,
                  }),
              }))}
              initialValues={{
                ...Object.keys(eraData).reduce((accumulator, currentValue) => {
                  return {
                    ...accumulator,
                    [currentValue]: true,
                  };
                }, {}),
              }}
              enableReinitialize
            />
          </Grid>
          {(Object.keys(filteredEraData).length > 0 && loading === 0) ? (
            <Grid item xs={8}>
              <Grid container spacing={0}>
                <Grid
                  item
                  xs={Object.keys(denialReasons).length > 0 ? 7 : 12}
                >
                  <EraChartDoughnut
                    filteredEraData={filteredEraData}
                    formValues={payersFormValues}
                  />
                </Grid>
                {Object.keys(denialReasons).length > 0 && (
                  <Grid item xs={5}>
                    <EraChartDenialReasons denialReasons={denialReasons} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid
              className={classes.gridItemEmpty}
              item
              xs={8}
            >
              {loading > 0 ? (
                <Progress />
              ) : (
                <Typography variant="caption" align="center">
                  No ERAs match filters
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
        {(Object.keys(filteredEraData).length > 0 && loading === 0) &&
          <div className={classes.tablesContainer}>
            {openedEraCheckNumber && !openedEra && (
              <Progress />
            )}
            {openedEra ? (
              <EraChartClaimsTable
                eraData={openedEra}
                checkNumber={openedEraCheckNumber}
                onERADownload={this.onERADownload}
                onBackButtonClick={this.onClaimsTableBackButtonClick}
              />
            ) : (
              <EraChartERATable
                eraData={filteredEraData}
                onERADetailsLoad={this.onERADetailsLoad}
              />
            )}
          </div>
        }
      </Paper>
    );
  }
}

EraChart.propTypes = {
  actions: PropTypes.shape({
    fetchEraStatistics: PropTypes.func.isRequired,
    fetchEra: PropTypes.func.isRequired,
  }).isRequired,
};

EraChart.defaultProps = {
  ordersStatisticsFormValues: {},
};

const mapStateToProps = (store) => {
  const payersFormValues = getFormValues('eraChartPayersForm')(store);
  const filterFormValues = getFormValues('eraChartFilterForm')(store);
  return {
    eraData: store.eraStatistics.data,
    loading: store.eraStatistics.loading,
    payersFormValues: payersFormValues || {},
    filterFormValues: filterFormValues ? {
      'Date From': filterFormValues['Date From'],
      'Date To': filterFormValues['Date To'],
      ...filterFormValues['Date Range'] && filterFormValues['Date Range'] === 'today' ? {
        'Date From': moment().subtract(1, 'day').format(dateFormat),
        'Date To': moment().format(dateFormat),
      } : {},
      ...filterFormValues['Date Range'] && filterFormValues['Date Range'] === 'week' ? {
        'Date From': moment().subtract(1, 'week').format(dateFormat),
        'Date To': moment().format(dateFormat),
      } : {},
      ...filterFormValues['Date Range'] && filterFormValues['Date Range'] === 'month' ? {
        'Date From': moment().subtract(1, 'month').format(dateFormat),
        'Date To': moment().format(dateFormat),
      } : {},
    } : {},
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ fetchEraStatistics, fetchEra, getEraDocument }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles, { withTheme: true })(EraChart),
);



// WEBPACK FOOTER //
// ./src/components/home/EraChart/EraChart.js